import React, { useState, useEffect, useCallback } from "react";
import { Trash2, PlusCircle } from "lucide-react";
import Modal from "../../../../Modal/Modal";

import "./TeamMembersList.css";

// --- API CONSTANTS ---
const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

// --- TEAM MEMBER MANAGEMENT VIEW ---
function TeamMembersList({
  clanId,
  eventId,
  team,
  loading,
  setLoading,
  apiMessage,
  setApiMessage,
  // New props for synchronization:
  onMemberChangeComplete, // (teamId, newCount) handler from parent
  memberListChanged, // Counter state from parent to trigger re-fetch
}) {
  // Consolidated Base URL for team members operations
  const TEAM_MEMBERS_BASE_URL = `${API_BASE_URL}/clan/${clanId}/events/${eventId}/teams/${team.id}/members`;

  // Current members on the team
  const [currentMembers, setCurrentMembers] = useState([]);
  // All eligible members available to be added, grouped by Discord ID for the dropdown
  const [eligibleMembers, setEligibleMembers] = useState([]);

  // State for the new member being added via the inline form
  const [newMemberData, setNewMemberData] = useState({
    osrs_ign_id: "", // Will hold the ID of the OSRS IGN to be added
    role: "Member", // Default role
  });

  const [memberLoading, setMemberLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  const [memberToDelete, setMemberToDelete] = useState(null);

  const availableRoles = ["Member", "Roller", "Leader"];

  // --- API CALLS & FETCHING ---

  const fetchMembers = useCallback(async () => {
    // Use local loading state only for this component's data fetching
    setMemberLoading(true);

    try {
      const response = await fetch(TEAM_MEMBERS_BASE_URL, {
        credentials: "include"
      });
      const data = await response.json();

      if (!response.ok) {
        setApiMessage({
          type: "error",
          text:
            data.error || "Failed to fetch team members and available list.",
        });
        setCurrentMembers([]);
        setEligibleMembers([]);
      } else {
        // This is the source of truth for all local rendering
        setCurrentMembers(data.team_members || []);
        setEligibleMembers(data.eligible_members || []);
      }
    } catch (error) {
      setApiMessage({
        type: "error",
        text: "Network error fetching members list.",
      });
      console.error("Team member fetch error:", error);
    } finally {
      setMemberLoading(false);
    }
  }, [TEAM_MEMBERS_BASE_URL, setApiMessage]);

  // 1. Initial Load: Fetch data on mount
  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // 2. SYNCHRONIZATION: Re-fetch only when the parent signals a global change has occurred.
  useEffect(() => {
    // We only need to re-fetch when this counter changes, ensuring all
    // sibling TeamMembersList components update their eligible list.
    if (memberListChanged > 0) {
      fetchMembers();
    }
  }, [memberListChanged, fetchMembers]);

  // --- HANDLERS ---

  const handleNewMemberChange = (e) => {
    const { name, value } = e.target;
    setNewMemberData((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (loading || !newMemberData.osrs_ign_id) return;

    setLoading(true);
    setFormError(null);
    setApiMessage({ type: null, text: null });

    const ignId = newMemberData.osrs_ign_id;
    const role = newMemberData.role;
    const url = TEAM_MEMBERS_BASE_URL;

    // Get IGN and Group for optimistic update and success message
    let addedIgn = "";
    let groupToUpdate = null;
    eligibleMembers.forEach((group) => {
      const ign = group.osrs_accounts.find(
        (account) => String(account.id) === String(ignId)
      );
      if (ign) {
        addedIgn = ign.ign;
        groupToUpdate = group;
      }
    });

    let response;

    try {
      response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ osrs_ign_id: ignId, role: role }),
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        setFormError(data.error || "Failed to add member.");
      } else {
        // --- CLIENT-SIDE STATE UPDATE (OPTIMISTIC) ---
        const newMember = data.member;
        let newCount = currentMembers.length;

        if (newMember) {
          // 1. Update currentMembers (add new member)
          setCurrentMembers((prev) => {
            const updatedMembers = [...prev, newMember];
            newCount = updatedMembers.length;
            return updatedMembers;
          });

          // 2. Optimistically update eligibleMembers (remove the specific OSRS IGN)
          setEligibleMembers((prev) =>
            prev.map((group) => {
              if (
                groupToUpdate &&
                group.discord_id === groupToUpdate.discord_id
              ) {
                return {
                  ...group,
                  osrs_accounts: group.osrs_accounts.filter(
                    (account) => String(account.id) !== String(ignId)
                  ),
                };
              }
              return group;
            })
          );
        }

        // On success:
        setNewMemberData({ osrs_ign_id: "", role: "Member" }); // Reset form
        setApiMessage({
          type: "success",
          text: `${addedIgn} added to team ${team.name} as a ${role}.`,
        });

        // CRUCIAL: Call parent handler to update count & signal global eligible list sync.
        onMemberChangeComplete(team.id, newCount + 1);

        // FIX: Explicitly set loading to false on the success path
        setLoading(false);
        return;
      }
    } catch (error) {
      setFormError("Network error. Failed to add member.");
      console.error("Add member error:", error);
    } finally {
      // This only runs on the error/catch path now, ensuring it gets reset.
      if (setLoading && !response?.ok) {
        setLoading(false);
      }
    }
  };

  // Set the member to be deleted and open the modal
  const handleRemoveMemberClick = (member) => {
    setApiMessage({ type: null, text: null }); // Clear messages before modal opens
    setMemberToDelete(member);
  };

  // Execute the delete action
  const executeRemoveMember = async () => {
    if (!memberToDelete || loading) return;

    const member = memberToDelete;

    setLoading(true);
    setApiMessage({ type: null, text: null });
    setMemberToDelete(null); // Close modal immediately

    const url = `${TEAM_MEMBERS_BASE_URL}/${member.id}`;

    // --- OPTIMISTIC DELETE ---
    const memberIdToRemove = member.id;
    let newCount = currentMembers.length;

    // Remove from local list immediately for instant visual feedback
    const originalCurrentMembers = currentMembers;
    setCurrentMembers((prev) => {
      const updatedMembers = prev.filter((m) => m.id !== memberIdToRemove);
      newCount = updatedMembers.length;
      return updatedMembers;
    });

    const memberBackup = member;

    // Find the corresponding Discord group to re-add the member to eligibleMembers
    const eligibleGroup = eligibleMembers.find(
      (g) => g.discord_id === member.discord_id
    );

    let response;

    try {
      response = await fetch(url, { method: "DELETE", credentials: "include" });

      if (!response.ok) {
        const data = await response.json();

        // REVERT: If API call fails, put the member back and show error
        setCurrentMembers(originalCurrentMembers);

        setApiMessage({
          type: "error",
          text: data.error || `Failed to remove ${member.osrs_ign}.`,
        });
      } else {
        // SUCCESS:
        // 1. Attempt to update eligibleMembers (add the OSRS IGN back)
        if (eligibleGroup) {
          setEligibleMembers((prev) =>
            prev.map((group) => {
              if (group.discord_id === eligibleGroup.discord_id) {
                const accountToAdd = {
                  id: member.osrs_ign_id,
                  ign: member.osrs_ign,
                };

                const exists = group.osrs_accounts.some(
                  (a) => a.id === accountToAdd.id
                );

                if (!exists) {
                  return {
                    ...group,
                    osrs_accounts: [...group.osrs_accounts, accountToAdd].sort(
                      (a, b) => a.ign.localeCompare(b.ign)
                    ),
                  };
                }
              }
              return group;
            })
          );
        }

        setApiMessage({
          type: "success",
          text: `${member.osrs_ign} removed from team.`,
        });

        // CRUCIAL: Call parent handler to update count & signal global eligible list sync.
        onMemberChangeComplete(team.id, newCount);

        // FIX: Explicitly set loading to false on the success path
        setLoading(false);
        return;
      }
    } catch (error) {
      // REVERT: If network error, put the member back
      setCurrentMembers(originalCurrentMembers);

      setApiMessage({ type: "error", text: "Network error removing member." });
      console.error("Remove member error:", error);
    } finally {
      // This only runs on the error/catch path now, ensuring it gets reset.
      if (setLoading && !response?.ok) {
        setLoading(false);
      }
    }
  };

  const closeDeleteModal = () => {
    setMemberToDelete(null);
    setApiMessage({ type: null, text: null });
  };

  const handleEditRole = async (memberId, newRole) => {
    if (loading) return;

    setLoading(true);
    setApiMessage({ type: null, text: null });

    const memberToUpdate = currentMembers.find((m) => m.id === memberId);
    if (!memberToUpdate) {
      setLoading(false);
      return;
    }

    // --- OPTIMISTIC UI UPDATE for Role Change ---
    const originalRole = memberToUpdate.role;
    setCurrentMembers((prev) =>
      prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m))
    );

    const url = `${TEAM_MEMBERS_BASE_URL}/${memberId}`;

    let response;

    try {
      response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: newRole }),
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        // Revert local change if API call fails
        setCurrentMembers((prev) =>
          prev.map((m) =>
            m.id === memberId ? { ...m, role: originalRole } : m
          )
        );

        setApiMessage({
          type: "error",
          text:
            data.error ||
            `Failed to update role for ${memberToUpdate.osrs_ign}.`,
        });
      } else {
        // SUCCESS: Local state is already updated (optimistically).
        setApiMessage({
          type: "success",
          text: `Role updated to ${newRole} for ${memberToUpdate.osrs_ign}.`,
        });
      }
    } catch (error) {
      // Revert local change if network error occurs
      setCurrentMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, role: originalRole } : m))
      );

      setApiMessage({ type: "error", text: "Network error updating role." });
      console.error("Update role error:", error);
    } finally {
      setLoading(false);
    }
  };

  // --- RENDER FUNCTIONS ---

  // Render the "Add Member" form row
  const renderAddMemberRow = () => {
    // Filter eligibleMembers to only include groups with at least one available OSRS account
    const availableGroups = eligibleMembers.filter(
      (group) => group.osrs_accounts && group.osrs_accounts.length > 0
    );

    const isDropdownDisabled = loading || availableGroups.length === 0;
    const hasAvailableAccounts = availableGroups.length > 0;

    return (
      <tr className="add-member-row">
        <td data-label="New Member">
          {/* Dropdown for available OSRS IGNs grouped by Discord user */}
          <select
            className="form-control"
            name="osrs_ign_id"
            value={newMemberData.osrs_ign_id}
            onChange={handleNewMemberChange}
            disabled={isDropdownDisabled}
          >
            <option value="">
              {isDropdownDisabled ? "No members available" : "Select IGN"}
            </option>

            {/* Iterate over the filtered groups */}
            {availableGroups.map((group) => (
              // Use <optgroup> for the non-selectable Discord user
              <optgroup
                key={group.user_id}
                label={`@${group.discord_username}`}
              >
                {group.osrs_accounts.map((account) => (
                  // Options are the selectable OSRS IGNs
                  <option key={account.id} value={account.id}>
                    {account.ign}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </td>
        <td data-label="Role">
          {/* Dropdown for Role */}
          <select
            className="form-control form-control-small"
            name="role"
            value={newMemberData.role}
            onChange={handleNewMemberChange}
            disabled={loading}
          >
            {availableRoles.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </td>
        <td data-label="Add" className="team-actions-cell">
          {/* Add Member Button */}
          <button
            className="btn-icon-small btn-success"
            onClick={handleAddMember}
            disabled={loading || !newMemberData.osrs_ign_id}
            title="Add Member to Team"
          >
            <PlusCircle size={16} />
          </button>
        </td>
      </tr>
    );
  };

  // Render the list of current members
  const renderCurrentMembers = () => {
    if (currentMembers.length === 0 && !memberLoading) {
      return (
        <tr>
          <td colSpan="3" className="table-placeholder-cell">
            <small>This team currently has no members.</small>
          </td>
        </tr>
      );
    }

    // Only render members if not actively fetching or if we have data
    if (memberLoading && currentMembers.length === 0) {
      return (
        <tr>
          <td colSpan="3" className="table-placeholder-cell">
            <small>Loading team members...</small>
          </td>
        </tr>
      );
    }

    return currentMembers.map((member) => (
      <tr key={member.id}>
        <td data-label="OSRS IGN">
          <span className="member-ign">{member.osrs_ign}</span>
        </td>
        <td data-label="Role">
          {/* Role selector for existing member */}
          <select
            className="form-control form-control-small"
            value={member.role}
            onChange={(e) => handleEditRole(member.id, e.target.value)}
            disabled={loading}
          >
            {availableRoles.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </td>
        <td data-label="Action" className="team-actions-cell">
          {/* Remove Member Button - Uses the new click handler */}
          <button
            className="btn-icon-small btn-danger"
            onClick={() => handleRemoveMemberClick(member)}
            disabled={loading}
            title="Remove Member"
          >
            <Trash2 size={16} />
          </button>
        </td>
      </tr>
    ));
  };

  // --- MAIN RENDER ---
  const availableGroups = eligibleMembers.filter(
    (group) => group.osrs_accounts && group.osrs_accounts.length > 0
  );
  const hasAvailableAccounts = availableGroups.length > 0;
  // Use local loading for initial load/fetch
  const isInitialOrLocalLoading =
    memberLoading && currentMembers.length === 0 && !hasAvailableAccounts;

  return (
    <>
      <div
        className="team-members-list-container"
        style={{
          borderLeft: `5px solid ${team.color || "#cccccc"}`,
          marginTop: "-1px",
          marginBottom: "0",
        }}
      >
        {/* Local error message for the ADD row */}
        {formError && (
          <div className="member-form-message error">{formError}</div>
        )}

        <div className="team-member-table-wrapper">
          <table className="teams-placeholder-table team-member-table">
            <thead>
              <tr>
                <th className="team-member-table-col-ign">OSRS IGN</th>
                <th className="team-member-table-col-role">Team Role</th>
                <th className="team-member-table-col-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {isInitialOrLocalLoading ? (
                <tr>
                  <td colSpan="3" className="table-placeholder-cell">
                    <small>Loading team members...</small>
                  </td>
                </tr>
              ) : (
                renderCurrentMembers()
              )}

              {/* The dynamic row for adding new members */}
              {hasAvailableAccounts && renderAddMemberRow()}

              {/* Display message if the team is empty and no one is available to add */}
              {!hasAvailableAccounts &&
                !isInitialOrLocalLoading && (
                  <tr>
                    <td colSpan="3" className="table-placeholder-cell">
                      <small>
                        All eligible clan members
                        are already assigned to an event team.
                      </small>
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MEMBER DELETION MODAL */}
      {memberToDelete && (
        <Modal
          isOpen={!!memberToDelete}
          title="Confirm Member Removal"
          message={
            <div className="delete-confirm-message">
              <p>
                Are you sure you want to <strong>remove</strong> the member:
              </p>
              <p className="team-name-to-delete">"{memberToDelete.osrs_ign}"</p>
              <p>
                from team <strong>{team.name}</strong>? This action can be
                undone by adding them back.
              </p>
            </div>
          }
          onConfirm={executeRemoveMember}
          onCancel={closeDeleteModal}
          // Use global loading for the confirm button to prevent double-click
          confirmButtonText={loading ? "Removing..." : "Remove Member"}
          confirmButtonClassName={
            loading ? "btn-danger btn-disabled" : "btn-danger"
          }
          cancelButtonText="Cancel"
        />
      )}
    </>
  );
}

export default TeamMembersList;
